import "./App.css";
import { useAuth } from "@saas-ui/auth";
import { Loader } from "./components/Loader";
import { useAppStore } from "./stores";
import { useEffect } from "react";
import AuthenticationPage from "./authentication/page";
import { Layout } from "./layout/page";
import { supabaseClient } from "./lib/supabase/client";
import { MissingUserRole } from "./layout/components/missing-user-role";
import { useLocation } from "wouter";
import { CommissionsProvider } from "@/providers/CommissionsProvider";
import { usePostHog } from "@posthog/react";
import { getCleanAuthenticatedBrowserUrl } from "./authentication/auth-redirect";

function App() {
  const auth = useAuth();
  const { isLoaded, fetch, reset, currentUser } = useAppStore();
  const [location, setLocation] = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    if (location === "/logout") {
      auth.logOut();
      reset();
      posthog?.reset();
      setLocation("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (currentUser?.id) {
      posthog?.identify(currentUser.id, {
        email: currentUser.email,
        name: `${currentUser.first_name ?? ""} ${currentUser.last_name ?? ""}`.trim(),
        role: currentUser.business_role,
        company_id: currentUser.company_id,
      });
      if (currentUser.company_id) {
        posthog?.group("company", String(currentUser.company_id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog, currentUser?.id]);

  useEffect(() => {
    if (!auth?.isAuthenticated || !currentUser?.id) return;
    const cleanUrl = getCleanAuthenticatedBrowserUrl(new URL(window.location.href));
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (cleanUrl !== currentUrl) {
      window.history.replaceState(window.history.state, "", cleanUrl);
    }
  }, [auth?.isAuthenticated, currentUser?.id]);

  useEffect(() => {
    let disposed = false;
    const pendingFetches = new Set<number>();
    const { data: authListener } = supabaseClient.auth.onAuthStateChange(
      (_event, session) => {
        // Supabase holds its auth lock while this callback runs. Defer any work
        // that can query Supabase until the callback has returned.
        const timeoutId = window.setTimeout(() => {
          pendingFetches.delete(timeoutId);
          if (!disposed) void fetch(session);
        }, 0);
        pendingFetches.add(timeoutId);
      }
    );

    return () => {
      disposed = true;
      pendingFetches.forEach(timeoutId => window.clearTimeout(timeoutId));
      pendingFetches.clear();
      authListener.subscription.unsubscribe();
    };
  }, [fetch]);

  return !auth || auth.isLoggingIn ? (
    <Loader />
  ) : auth.isAuthenticated ? (
    isLoaded() ? (
      <CommissionsProvider>
        <Layout />
      </CommissionsProvider>
    ) : currentUser && currentUser.role === null ? (
      <CommissionsProvider>
        <MissingUserRole />
      </CommissionsProvider>
    ) : (
      <Loader />
    )
  ) : (
    <CommissionsProvider>
      <AuthenticationPage />
    </CommissionsProvider>
  );
}

export default App;
